// api/API.js
import axios from "axios";
import {
  getCurrentUser,
  getToken,
  loginUser,
  logoutUser,
} from "../utility/UserUtils";

const API_BASE = process.env.REACT_APP_API_URL;

// Axios instances
const adminAPI = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // for Payload Admin-only requests
});
const memberAPI = axios.create({
  baseURL: API_BASE,
  withCredentials: false,
});

// Automatically attach JWT token for member requests
memberAPI.interceptors.request.use((config) => {
  config.metadata = { startTime: new Date() };
  const token = localStorage.getItem("membersToken");
  console.log("[API] Using token:", token);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

memberAPI.interceptors.response.use(
  (response) => {
    const endTime = new Date();
    const duration = endTime - response.config.metadata.startTime;
    console.log(
      `✅ [API] ${response.config.method.toUpperCase()} ${
        response.config.url
      } → ${duration} ms`
    );
    return response;
  },
  (error) => {
    const endTime = new Date();
    const duration = endTime - (error.config?.metadata?.startTime || endTime);
    console.error(
      `❌ [API] ${error.config?.method?.toUpperCase()} ${
        error.config?.url
      } → ${duration} ms`,
      error.response?.data || error.message
    );
    return Promise.reject(error);
  }
);

// ------------------ AUTH ------------------

// Member login
export const loginMember = async (email, password) => {
  const res = await memberAPI.post("/members/login", { email, password });
  if (res.data?.token && res.data?.user) {
    loginUser(res.data.user, res.data.token);
  }
  return res.data;
};

// Member logout
export const logoutMember = async () => {
  try {
    await memberAPI.post("/members/logout");
  } catch (err) {
    console.error("Logout request failed:", err);
  }
  logoutUser(); // clears localStorage and redirects
};

// ------------------ CRUD HELPERS ------------------

// CREATE
export const create = async (endpoint, payload, config = {}) => {
  const res = await memberAPI.post(endpoint, payload, config);
  return res.data;
};

// READ
export const getAll = async (endpoint, params = {}) => {
  const res = await memberAPI.get(endpoint, { params });
  return res.data;
};

export const getByID = async (endpoint, id, params = {}) => {
  const res = await memberAPI.get(`/${endpoint}/${id}`, { params });
  return res.data;
};

export const getByQuery = async (endpoint, query) => {
  const res = await memberAPI.get(`${endpoint}?${query}`);
  return res.data;
};

// UPDATE
export const update = async (endpoint, payload, config = {}) => {
  const res = await memberAPI.patch(endpoint, payload, config);
  return res.data;
};

// PATCH (partial update)
export const patch = async (endpoint, payload, config = {}) => {
  const res = await memberAPI.patch(endpoint, payload, config);
  return res.data;
};

// ------------------ EVENT REGISTRATION ------------------

// Register a user after payment verification
export const registerForEvent = async (paymentDetails, eventId, member) => {
  try {
    const res = await memberAPI.post("/event-payment-verify", {
      ...paymentDetails,
      eventId,
      member: {
        id: member.id,
        firstName: member.firstName,
        lastName: member.lastName,
        email: member.email,
        cohort: member.cohort,
      },
    });
    return res.data;
  } catch (err) {
    console.error("Registration failed:", err.response?.data || err.message);
    throw err;
  }
};

// Create a payment order
export const createOrder = async ({ amount, eventId }) => {
  try {
    const res = await memberAPI.post("/create-order", {
      amount,
      eventId,
      member: getCurrentUser()?.id,
    });
    return res.data;
  } catch (err) {
    console.error("Create order failed:", err.response?.data || err.message);
    throw err;
  }
};
// api/API.js (reuse)
export const completeProfile = async (memberId, payload) => {
  const res = await update(`/members/${memberId}`, payload);
  return res;
};
